// Third-party
import round from 'lodash/round';
import moment from 'moment';

// Utils
import { deepClone } from './objects';
import { fundToComparisonItem, stockToComparisonItem, isBenchmark, getCommonDataPoints } from './comparison';
import { HOLDING_TYPES, ROLLING_INTERVALS } from './portfolios';
import { trimRollingData } from './calculation-utils';
import { formatLocalizedString, formatMessage, formatPercentage, formatNumber, LOCALE, generateLocalizedString, formatCurrency } from './i18n';
import { CHART_STARTING_CAPITAL, COMPARISON_ITEM_TYPES, ComparisonToolbarKeys, Interval, PERIODS_VALUE_CI, PERIODS_VALUE_INC, TIME_PERIODS } from './constants';
import portfolioUtils from './portfolio';
import { REBALANCING_PERIODS } from './portfolio/utils';
import { periodToMonths, monthsToPeriod } from './time';
import { getMaxEndDate } from './dates';

// Types
import { ScatterPlotOptions } from '../views/components/charts/scatter-plot/ScatterPlot';
import * as ProposalNS from '../modules/proposal/Proposal';
import { getComparisonItemHoldings } from './comparison/items';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import DonutsModuleData from '@/types/donuts-module-data';
import Fund from '@/types/fund';
import AppRegion from '@/types/app-region';
import Stock from '@/types/stock';
import Timestamp from '@/types/timestamp';
import RollingIntervals from '@/types/rolling-intervals';
import Nullable from '@/types/nullable';
import RebalanceFrequency from '@/types/rebalance-frequency';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LocalizedString from '@/types/localized-string';
import TimePeriods from '@/types/time-periods';
import GrowthChartOptions from '@/types/growth-chart-options';
import RiskPeriods from '@/types/risk-periods';
import CalendarYearPeriod from '@/types/calendar-year-period';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';

export const MAX_START_DATE = moment().subtract(40, 'years').toDate();

const ROLLING_INTERVAL_MINIMUM_YEARS_THRESHOLD = 5;

export type ChartItem = ComparisonItem & {
  isSubHolding?: boolean;
  isShort?: boolean;
};

type GenericFormatterOptions = {
  formatter: () => string;
};

type DateLabelFormatters = {
  months: string[];
  shortMonths: string[];
  shortWeekdays: string[];
  weekdays: string[];
};

type DecimalFormatterOptions = {
  getFormatter: (decimals?: number) => () => string;
};

type LabelFormattersByLocale = {
  dollars: Record<Locale, GenericFormatterOptions>;
  dollarsNoSuffix: Record<Locale, GenericFormatterOptions>;
  decimal: Record<Locale, DecimalFormatterOptions>;
  date: Record<Locale, GenericFormatterOptions>;
  lang: (locale: Locale) => DateLabelFormatters;
};

// Vague TODO - investigate the differences between this formatter and the `formatCurrency` utils.
// Seems like they were kept separate on purpose, but struggling to understand why.
const chartNumberFormatters = {
  EN: (value: number) => {
    if (value < 1000) {
      return `${value}`;
    }

    if (value < 1000000) {
      const v = value / 1000;
      return `${v}k`;
    }

    const v = value / 1000000;
    return `${v}M`;
  },
  FR: (value: number) => {
    let out: string = '';
    if (value < 1000) {
      out = `${value}`;
    } else if (value < 1000000) {
      const v = value / 1000;
      out = `${v} k`;
    } else {
      const v = value / 1000000;
      out = `${v} M`;
    }
    out = out.replace(',', ' ').replace('.', ',');
    return `${out}`;
  },
};

const chartDateFormatters = {
  EN: (timestamp: number) => {
    const d: Date = new Date(timestamp);
    const year: number = d.getFullYear() % 100;
    const m: number = d.getMonth();
    let month: string = '';
    switch (m) {
      case 0:
        month = 'Jan';
        break;
      case 1:
        month = 'Feb';
        break;
      case 2:
        month = 'Mar';
        break;
      case 3:
        month = 'Apr';
        break;
      case 4:
        month = 'May';
        break;
      case 5:
        month = 'Jun';
        break;
      case 6:
        month = 'Jul';
        break;
      case 7:
        month = 'Aug';
        break;
      case 8:
        month = 'Sep';
        break;
      case 9:
        month = 'Oct';
        break;
      case 10:
        month = 'Nov';
        break;
      case 11:
        month = 'Dec';
        break;
    }

    const out = `${month} '${year}`;
    return String(out);
  },
  FR: (timestamp: number) => {
    const d: Date = new Date(timestamp);
    const year: number = d.getFullYear() % 100;
    const m: number = d.getMonth();
    let month: string = '';
    switch (m) {
      case 0:
        month = 'janv';
        break;
      case 1:
        month = 'fév';
        break;
      case 2:
        month = 'mars';
        break;
      case 3:
        month = 'avril';
        break;
      case 4:
        month = 'mai';
        break;
      case 5:
        month = 'juin';
        break;
      case 6:
        month = 'juil';
        break;
      case 7:
        month = 'août';
        break;
      case 8:
        month = 'sept';
        break;
      case 9:
        month = 'oct';
        break;
      case 10:
        month = 'nov';
        break;
      case 11:
        month = 'déc';
        break;
    }

    const out = `${month} '${year}`;
    return String(out);
  },
};

export const LABEL_FORMATTERS: LabelFormattersByLocale = {
  dollars: {
    [LOCALE.EN_CA]: {
      formatter(): string {
        const self: any = this;
        return chartNumberFormatters.EN(self.value);
      },
    },
    [LOCALE.EN_US]: {
      formatter(): string {
        const self: any = this;
        return chartNumberFormatters.EN(self.value);
      },
    },
    [LOCALE.FR_CA]: {
      formatter(): string {
        const self: any = this;
        return chartNumberFormatters.FR(self.value);
      },
    },
  },
  dollarsNoSuffix: {
    [LOCALE.EN_CA]: {
      formatter(): string {
        const self: any = this;
        return formatCurrency(self.value, 0, undefined, false, LOCALE.EN_CA);
      },
    },
    [LOCALE.EN_US]: {
      formatter(): string {
        const self: any = this;
        return formatCurrency(self.value, 0, undefined, false, LOCALE.EN_US);
      },
    },
    [LOCALE.FR_CA]: {
      formatter(): string {
        const self: any = this;
        return formatCurrency(self.value, 0, undefined, false, LOCALE.FR_CA);
      },
    },
  },
  decimal: {
    [LOCALE.EN_CA]: {
      getFormatter(decimals?: number) {
        return function (this: any) {
          return formatNumber(this.value, decimals, LOCALE.EN_CA);
        };
      },
    },
    [LOCALE.EN_US]: {
      getFormatter(decimals?: number) {
        return function (this: any) {
          return formatNumber(this.value, decimals, LOCALE.EN_US);
        };
      },
    },
    [LOCALE.FR_CA]: {
      getFormatter(decimals?: number) {
        return function (this: any) {
          return formatNumber(this.value, decimals, LOCALE.FR_CA);
        };
      },
    },
  },
  date: {
    [LOCALE.EN_CA]: {
      formatter(): string {
        const self: any = this;
        return chartDateFormatters.EN(self.value);
      },
    },
    [LOCALE.EN_US]: {
      formatter(): string {
        const self: any = this;
        return chartDateFormatters.EN(self.value);
      },
    },
    [LOCALE.FR_CA]: {
      formatter(): string {
        const self: any = this;
        return chartDateFormatters.FR(self.value);
      },
    },
  },
  lang: (locale: Locale) => {
    switch (locale) {
      case LOCALE.FR_CA:
        return {
          months: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
          shortMonths: ['janv', 'fév', 'mars', 'avril', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'],
          shortWeekdays: ['di', 'lu', 'ma', 'me', 'je', 've', 'sa'],
          weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        };
      default:
        return {
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          shortWeekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        };
    }
  },
};

/**
 * Fill in remaining donut data with 'Other' value to display donut as 100% full.
 * ex. If data sums to 80%, will fill in remaining 20% as 'Other'
 *
 * @param {DonutsModuleData} data Donut module data for top values.
 * @param {string} label Label to display next to the value
 * @return {DonutsModuleData} data with additional 'Other' object filling remaining value (if needed).
 */
export const fillRemainingWithOther = (data: DonutsModuleData, label: string) => {
  const clone = deepClone(data);

  clone.forEach(d => {
    const sum = d.values.reduce((a, b) => a + b.value, 0);

    if (sum && sum < 100) {
      const remainingPercentage = round(100 - sum, 2);
      d.values.push({ label, value: remainingPercentage });
    }
  });

  return clone;
};

/**
 * Gets the date of the latest graphed data point.
 *
 * @param {number[][]} series Series graphed.
 * @returns The date of the latest graphed data point.
 */
export const getLatestGraphedDate = (series: number[][]) => {
  const timestamp = series.reduce((date, data) => {
    if (data.length === 0) {
      return date;
    }

    return Math.max(date, data[0]);
  }, -Infinity);

  return new Date(timestamp);
};

/**
 * Merge comparison items with items of the expanded portfolios into chart items.
 *
 * @param items Comparison items
 * @param portfolioHoldingItems Items of the expanded portfolios
 */
export const mergeChartItems = (items: ComparisonItem[], portfolioHoldingItems: ComparisonItem[]): ChartItem[] =>
  items.concat(
    portfolioHoldingItems.map(item => ({
      ...item,
      isSubHolding: true,
    })),
  );

/**
 * Generate the comparison items for all expanded portfolio items.
 *
 * @param items Comparison items
 * @param data Dictionaries of funds and stocks to expand the portfolio items.
 */
export const getExpandedPortfolioItems = (
  items: ComparisonItem[],
  appRegion: AppRegion,
  data?: Dictionary<{ funds: Dictionary<Fund>; stocks: Dictionary<Stock> }>,
) => {
  if (!data) {
    return [];
  }

  let holdingItems: ComparisonItem[] = [];

  const expandedPortfolios = items.filter(item => item.isExpanded && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

  expandedPortfolios.forEach(item => {
    const funds = item.holdings.filter(h => h.type === HOLDING_TYPES.FUND);
    const customYields = item.holdings.filter(h => h.type === HOLDING_TYPES.CUSTOM_YIELD);

    for (const fund of [...funds, ...customYields]) {
      if (data[item._id].funds[fund.id] != null) {
        const fundItem = fundToComparisonItem(data[item._id].funds[fund.id], appRegion);
        holdingItems.push({
          ...fundItem,
          color: item.color,
        });
      }
    }

    const stocks = item.holdings.filter(h => h.type === HOLDING_TYPES.STOCK);

    for (const stock of stocks) {
      if (data[item._id].stocks[stock.id] != null) {
        const stockItem = stockToComparisonItem(data[item._id].stocks[stock.id], undefined, undefined, appRegion);
        holdingItems.push({
          ...stockItem,
          color: item.color,
        });
      }
    }
  });

  // Filter duplicates
  holdingItems = holdingItems.filter((holdingItem, i, arr) => arr.map(value => value._id).indexOf(holdingItem._id) === i);

  return holdingItems;
};

export const getDashStyle = (item: { type: string; isSubHolding?: boolean }, isFx?: boolean, isShortHistory?: boolean, isAdditionalScenario?: boolean) => {
  if (isBenchmark(item) || (isAdditionalScenario && !isShortHistory)) {
    return 'Dash' as const;
  }

  if (isFx) {
    return 'LongDashDot' as const;
  }

  if (isShortHistory && !isAdditionalScenario) {
    return 'LongDash' as const;
  }

  if (isAdditionalScenario && isShortHistory) {
    return 'ShortDot' as const;
  }

  if (item.isSubHolding) {
    return 'Solid' as const;
  }

  return 'Solid' as const;
};

export const getLineWidth = (item: { type: string; isSubHolding?: boolean }, isFX?: boolean, isAdditionalScenario?: boolean) => {
  if (isBenchmark(item) || item.isSubHolding || isFX || isAdditionalScenario) {
    return 1;
  }

  return 2;
};

export const getReturnOnInvestment = (performance: number[][] | undefined, initialCapital: number) => {
  let capital = initialCapital;

  if (!performance) {
    return [];
  }

  return performance.map((period: number[], i: number) => {
    // Calculate growth since last period, skip if it's the first period
    if (i > 0) {
      capital += capital * (period[1] / 100);
    }

    return [period[0], capital] as [number, number];
  });
};

/**
 *
 * @param item Chart item
 * @param interval Rolling data interval (weekly or monthly)
 * @param startPoint Start date to trim the series
 * @param breakingPoint Timestamp of the earliest common inception of the item's holdings
 * @param isFX If base currency reporting is enabled and that this item is a CAD-converted item.
 */
export const getSeriesBeforeAfter = (
  item: { name: LocalizedString; type: string; isSubHolding?: boolean },
  data: [Timestamp, number][],
  startPoint: number,
  breakingPoint: number,
  isFX?: boolean,
  overrideItemName?: string,
) => {
  const itemName = overrideItemName || formatLocalizedString(item.name);

  return {
    before: {
      itemType: item.type,
      data: trimRollingData(data, startPoint, breakingPoint),
      name: `${itemName} - ${formatMessage('Charts.Incomplete')}` || '',
      dashStyle: getDashStyle(item, isFX, true),
      lineWidth: getLineWidth(item, isFX),
      type: 'line' as const,
    },
    after: {
      itemType: item.type,
      data: trimRollingData(data, breakingPoint, Date.now()),
      name: itemName || '',
      dashStyle: getDashStyle(item, isFX, false),
      lineWidth: getLineWidth(item, isFX),
      type: 'line' as const,
    },
  };
};

export const trimComparisonItemsData = (
  items: ComparisonItem[],
  startDate: Date,
  endDate: Date,
  interval: RollingIntervals,
  shortHistory: boolean = false,
  ignoreCommonDataPoints: boolean = false,
): ChartItem[] => {
  const startDates = ignoreCommonDataPoints
    ? { weekly: 0, monthly: 0 }
    : getCommonDataPoints(items, interval as unknown as Interval, startDate, 'ASC', shortHistory);
  const startCutoff = startDates;

  return items.map(item => {
    const clone = deepClone(item);

    const newItem: ChartItem = {
      ...clone,
    };

    const holdingsData = getComparisonItemHoldings(clone, true).map(holding => ({
      ...holding,
      rolling: {
        weekly: trimRollingData(holding.rolling && holding.rolling.weekly, startCutoff.weekly, endDate.getTime()),
        monthly: trimRollingData(holding.rolling && holding.rolling.monthly, startCutoff.monthly, endDate.getTime()),
      },
      baseRolling: {
        weekly: trimRollingData(holding.baseRolling && holding.baseRolling.weekly, startCutoff.weekly, endDate.getTime()),
        monthly: trimRollingData(holding.baseRolling && holding.baseRolling.monthly, startCutoff.monthly, endDate.getTime()),
      },
    }));

    if (newItem.overrideHoldings != null) {
      newItem.overrideHoldings = holdingsData;
    } else {
      newItem.holdings = holdingsData;
    }

    if (shortHistory) {
      const timestamp = moment.utc(startDate).startOf('day').weekday(5).valueOf();

      newItem.isShort = newItem.holdings.some(h => !!h.rolling && !!h.rolling[interval].length && h.rolling[interval][0][0] > timestamp);
    }

    return newItem;
  });
};

export const getShortHistoryTooltipMessage = (enabled?: boolean) => formatMessage(`Disclaimers.ShortHistory${enabled ? 'Enabled' : 'Disabled'}`);

export const getRollingIntervalFromDates = (start: Nullable<Date>, end: Date, items: Array<ComparisonItem>, useMonthly?: boolean, useLongHistory?: boolean) => {
  if (useMonthly) {
    return ROLLING_INTERVALS.MONTHLY;
  }
  if (!start) {
    const commonDataPoint = new Date(getCommonDataPoints(items, Interval.Weekly, undefined, 'ASC', useLongHistory).weekly);
    return moment(end).diff(moment(commonDataPoint), 'years') > ROLLING_INTERVAL_MINIMUM_YEARS_THRESHOLD ? ROLLING_INTERVALS.MONTHLY : ROLLING_INTERVALS.WEEKLY;
  }

  return moment(end).diff(moment(start), 'years') > ROLLING_INTERVAL_MINIMUM_YEARS_THRESHOLD ? ROLLING_INTERVALS.MONTHLY : ROLLING_INTERVALS.WEEKLY;
};

export const getRiskRewardChartOptions = (): Partial<ScatterPlotOptions> => ({
  tooltip: {
    // Disable default tooltip background and go for fully-custom tooltips,
    // allowing us to work with the z-index and have the tooltips on top of the
    // crosshairs.
    backgroundColor: 'rgba(0,0,0,0)',
    shadow: false,
    borderWidth: 0,
    useHTML: true,
    formatter() {
      let symbol = '';
      switch (this.point.marker && this.point.marker.symbol) {
        case 'diamond':
          symbol = '\u25C6';
          break;
        case 'circle':
          symbol = '\u25CF';
          break;
      }

      return `
          <div style="background-color:#000000;border:1px solid #000000;padding:8px;">
            <span style="color:${this.point.color};">${symbol}</span>
            <span data-private data-dd-privacy="mask" style="max-width: 200px; white-space: nowrap;text-overflow: ellipsis; overflow: hidden; display: inline-block; vertical-align: text-bottom; color:#ffffff;">
              ${this.point.name}
            </span>
            <br />
            <span style="color:#A4A7B7;">
              ${formatMessage('RiskRewardPlot.Tooltip.LabelX')}
            </span>
            <span style="color:#A4A7B7;">
              ${this.point.x && formatNumber(this.point.x, 2)}
            </span>
            <br />
            <span style="color:#A4A7B7;">
              ${formatMessage('RiskRewardPlot.Tooltip.LabelY')}
            </span>
            <span style="color:#A4A7B7;">
              ${this.point.y && formatPercentage(this.point.y, 2)}
            </span>
          </div>
        `;
    },
  },
});

export const getGraphData = (
  items: ChartItem[],
  startDate: Nullable<Date>,
  endDate: Date,
  interval: RollingIntervals,
  rebalancePeriod?: RebalanceFrequency,
  shortHistory?: boolean,
): ChartItem[] => {
  const trimmedItems = trimComparisonItemsData(items, startDate || MAX_START_DATE, endDate, interval, shortHistory);

  // TODO -- Show visual cue if we're trimming more than the asked range
  for (const item of trimmedItems) {
    const fees = (item.discretionaryMer || 0) + (item.tax || 0);

    const holdings = getComparisonItemHoldings(item, true);
    item.performance.rolling = portfolioUtils.calculateGrowth(holdings, fees, rebalancePeriod, shortHistory);

    if (item.performanceBase) {
      const holdings = deepClone(item.holdings).map(h => ({ ...h, rolling: h.baseRolling }));
      item.performanceBase.rolling = portfolioUtils.calculateGrowth(holdings, fees, rebalancePeriod, shortHistory);
    }
  }

  return trimmedItems;
};

export const getGraphStartDate = (items: ChartItem[], startDate?: Nullable<Date>, showBaseCurrencies?: boolean) => {
  let earliestStartDate = startDate || moment().startOf('month').subtract(36, 'month').toDate();

  // If any of the items has less data we'll set the default range to 1Y
  for (const item of items) {
    const performances = showBaseCurrencies ? [item.performance, item.performanceBase] : [item.performance];

    for (const performance of performances) {
      const weeklyRolling = (performance && performance.rolling && performance.rolling.weekly) || [];
      if (!startDate && weeklyRolling.length && weeklyRolling[0][0] > earliestStartDate.getTime()) {
        earliestStartDate = moment().startOf('month').subtract(12, 'month').toDate();
      }
    }
  }

  return earliestStartDate;
};

export const convertConfigOptionsToHighcharts = (type: string, configOptions: Partial<ProposalNS.ChartConfigOptions>, locale?: Locale): Highcharts.Options => {
  const { chart, xAxis, yAxis, plotOptions, series, legend, tooltip } = configOptions;
  const plotOptionsType: ProposalNS.PlotOptions | undefined = plotOptions && plotOptions[type];

  return {
    chart: {
      type,
      backgroundColor: (chart && chart.backgroundColor) || 'transparent',
      height: chart && chart.height,
      margin: chart && chart.margin,
      width: chart && chart.width,
      className: chart && chart.className,
      inverted: chart && chart.inverted,
    },
    xAxis: {
      plotBands: xAxis && xAxis.plotBands,
      plotLines: xAxis && xAxis.plotLines,
      type: xAxis && xAxis.type ? xAxis.type : 'datetime',
      min: xAxis && xAxis.min,
      title: {
        text: xAxis && xAxis.title && formatLocalizedString(xAxis.title.text, locale),
      },
      gridLineWidth: xAxis && xAxis.gridLineWidth,
      labels: {
        rotation: xAxis && xAxis.labels && xAxis.labels.rotation,
        format: xAxis && xAxis.labels && xAxis.labels.format,
        style: {
          fontSize: xAxis && xAxis.labels && xAxis.labels.style && xAxis.labels.style.fontSize,
        },
      },
      lineWidth: xAxis && xAxis.lineWidth,
      lineColor: xAxis && xAxis.lineColor,
      reversed: xAxis && xAxis.reversed,
    },
    yAxis: {
      showLastLabel: yAxis && yAxis.showLastLabel,
      opposite: yAxis && yAxis.opposite,
      min: yAxis && yAxis.min,
      title: {
        text: yAxis && yAxis.title && formatLocalizedString(yAxis.title.text, locale),
      },
      stackLabels: {
        enabled: yAxis && yAxis.stackLabels && yAxis.stackLabels.enabled,
      },
      gridLineWidth: yAxis && yAxis.gridLineWidth,
      labels: {
        y: (yAxis && yAxis.labels && yAxis.labels.y) || 3,
        x: (yAxis && yAxis.labels && yAxis.labels.x) || -8,
        rotation: yAxis && yAxis.labels && yAxis.labels.rotation,
        format: yAxis && yAxis.labels && yAxis.labels.format,
        style: {
          fontSize: yAxis && yAxis.labels && yAxis.labels.style && yAxis.labels.style.fontSize,
        },
      },
      lineWidth: yAxis && yAxis.lineWidth,
      lineColor: yAxis && yAxis.lineColor,
      startOnTick: yAxis !== undefined && yAxis.startOnTick !== undefined ? yAxis.startOnTick : true,
      tickPixelInterval: yAxis !== undefined && yAxis.tickPixelInterval !== undefined ? yAxis.tickPixelInterval : 72,
      tickInterval: yAxis && yAxis.tickInterval,
    },
    plotOptions: {
      [type]: {
        borderWidth: plotOptionsType && plotOptionsType.borderWidth,
        shadow: plotOptionsType && plotOptionsType.shadow,
        center: (plotOptionsType && plotOptionsType.center) || [null, null],
        innerSize: (plotOptionsType && plotOptionsType.innerSize) || '50%',
        size: plotOptionsType && plotOptionsType.size,
        pointWidth: plotOptionsType && plotOptionsType.pointWidth,
        pointPadding: plotOptionsType !== undefined && plotOptionsType.pointPadding !== undefined ? plotOptionsType.pointPadding : 0.1,
        groupPadding: plotOptionsType !== undefined && plotOptionsType.groupPadding !== undefined ? plotOptionsType.groupPadding : 0.2,
        dataLabels: {
          enabled: plotOptionsType && plotOptionsType.dataLabels && plotOptionsType.dataLabels.enabled,
          format: plotOptionsType && plotOptionsType.dataLabels && plotOptionsType.dataLabels.format,
        },
        animation: plotOptionsType && plotOptionsType.animation,
        getExtremesFromAll: (plotOptionsType && plotOptionsType.getExtremesFromAll) || false,
      },
      series: {
        enableMouseTracking: plotOptions && plotOptions.series && plotOptions.series.enableMouseTracking,
        animation: plotOptions && plotOptions.series && plotOptions.series.animation,
        label: { enabled: plotOptions && plotOptions.series && plotOptions.series.label && plotOptions.series.label.enabled },
        dataLabels: {
          enabled: plotOptions && plotOptions.series && plotOptions.series.dataLabels && plotOptions.series.dataLabels.enabled,
          format: plotOptions && plotOptions.series && plotOptions.series.dataLabels && plotOptions.series.dataLabels.format,
        },
      },
    },
    series,
    legend: {
      enabled: legend && legend.enabled,
    },
    tooltip: {
      enabled: tooltip && tooltip.enabled,
    },
    // Defaulted Options
    title: undefined,
    credits: { enabled: false },
  };
};

export const getDefaultToolbarOption = (
  proposal: ProposalNS.Proposal,
  comparisonToolbarKey?: ComparisonToolbarKeys,
  defaultValues?: ProposalNS.ToolbarOptionDefaultValues | undefined,
  periods?: TIME_PERIODS[],
): ProposalToolbarOption => {
  let capital = CHART_STARTING_CAPITAL;
  let endDate = proposal.monthEndDataOption ? getMaxEndDate(proposal.monthEndDataOption) : moment().toDate();
  let startDate: Date | null = moment(endDate).startOf('month').subtract(36, 'month').toDate();
  let rebalancePeriod = REBALANCING_PERIODS.DAILY;
  let periodLabel: LocalizedString | undefined;
  let useMonthly = false;
  let targetROI;
  let monthsRange;

  const getStartDateForMonthRange = (eDate: Date | null, monthsRange: Nullable<number>) =>
    monthsRange !== PERIODS_VALUE_CI && monthsRange !== PERIODS_VALUE_CI ? moment(eDate).startOf('month').subtract(monthsRange, 'months').toDate() : null;

  if (defaultValues !== undefined) {
    if (defaultValues.initialCapital !== undefined) {
      capital = defaultValues.initialCapital;
    }

    if (defaultValues.timePeriod !== undefined) {
      monthsRange = periodToMonths(defaultValues.timePeriod as TimePeriods);
      if (monthsRange !== PERIODS_VALUE_INC && monthsRange !== PERIODS_VALUE_CI) {
        startDate = moment(endDate).startOf('month').subtract(monthsRange, 'month').toDate();
      } else {
        startDate = null;
      }
      periodLabel = generateLocalizedString(`Common.Periods.${defaultValues.timePeriod}`);
    }

    if (defaultValues.rebalancePeriod !== undefined) {
      rebalancePeriod = REBALANCING_PERIODS[defaultValues.rebalancePeriod];
    }

    if (defaultValues.useMonthly !== undefined) {
      useMonthly = defaultValues.useMonthly;
    }
  }

  const { comparisonToolbarOptions } = proposal;

  const handleGrowthCharts = (chartOption: GrowthChartOptions | undefined) => {
    // Always override start date for growth. When INC is
    // selected, startDate is intentionally left as null,
    // and it's expected that the value should remain null.
    const localEndDate = chartOption?.endDate || endDate;
    const localStartDate = chartOption?.startDate ?? null;

    if (localEndDate != null) {
      if (localStartDate != null) {
        monthsRange = moment(localEndDate).diff(localStartDate, 'months');
        periodLabel = generateLocalizedString(`Common.Periods.${monthsToPeriod(monthsRange, proposal.monthEndDataOption)}`);
        startDate = localStartDate;
      } else {
        startDate = null;
        if (periods != null) {
          monthsRange = periods.includes(TIME_PERIODS.INC) ? TIME_PERIODS.INC : TIME_PERIODS.CI;
          periodLabel = generateLocalizedString(`Common.Periods.${periods.includes(TIME_PERIODS.INC) ? TIME_PERIODS.INC : TIME_PERIODS.CI}`);
        }
      }
    }

    if (chartOption && chartOption.capital) {
      ({ capital } = chartOption);
    }

    if (chartOption && chartOption.targetROI) {
      ({ targetROI } = chartOption);
    }

    if (chartOption && chartOption.useMonthly) {
      useMonthly = chartOption.useMonthly;
    }
  };

  const handleRiskMetrics = (defaultRiskPeriod?: RiskPeriods) => {
    monthsRange = periodToMonths(defaultRiskPeriod || '3Y');
    startDate = getStartDateForMonthRange(endDate, monthsRange);

    if (!periodLabel) {
      periodLabel = generateLocalizedString(`Common.Periods.${defaultRiskPeriod}`);
    }
  };

  const handleCalendarYearReturns = (defaultCalendarYearPeriod: CalendarYearPeriod) => {
    monthsRange = periodToMonths(defaultCalendarYearPeriod);
    startDate = getStartDateForMonthRange(endDate, monthsRange);

    if (!periodLabel) {
      periodLabel = generateLocalizedString(`Common.Periods.${defaultCalendarYearPeriod}`);
    }
  };

  switch (comparisonToolbarKey) {
    case ComparisonToolbarKeys.GROWTH: {
      const chartOption = comparisonToolbarOptions && comparisonToolbarOptions.growth;
      handleGrowthCharts(chartOption);
      break;
    }
    case ComparisonToolbarKeys.FUND_GROWTH: {
      let chartOption = comparisonToolbarOptions && comparisonToolbarOptions.fundGrowth;
      if (!chartOption) {
        chartOption = comparisonToolbarOptions && comparisonToolbarOptions.growth;
      }
      handleGrowthCharts(chartOption);
      break;
    }
    case ComparisonToolbarKeys.PORTFOLIO_GROWTH: {
      let chartOption = comparisonToolbarOptions && comparisonToolbarOptions.portfolioGrowth;
      if (!chartOption) {
        chartOption = comparisonToolbarOptions && comparisonToolbarOptions.growth;
      }
      handleGrowthCharts(chartOption);
      break;
    }
    case ComparisonToolbarKeys.GROUP_GROWTH: {
      let chartOption = comparisonToolbarOptions && comparisonToolbarOptions.groupGrowth;
      if (!chartOption) {
        chartOption = comparisonToolbarOptions && comparisonToolbarOptions.growth;
      }
      handleGrowthCharts(chartOption);
      break;
    }
    case ComparisonToolbarKeys.COMPARISON_CORRELATION_MATRIX:
    case ComparisonToolbarKeys.CORRELATION_MATRICES:
    case ComparisonToolbarKeys.RISK_REWARD: {
      const chartOption = comparisonToolbarOptions && comparisonToolbarOptions.riskReward;

      if (chartOption && chartOption.monthsRange) {
        monthsRange = chartOption.monthsRange;
        startDate = getStartDateForMonthRange(endDate, monthsRange);
        const period = monthsToPeriod(chartOption.monthsRange, proposal.monthEndDataOption) as TimePeriods;
        if (!periodLabel) {
          periodLabel = generateLocalizedString(`Common.Periods.${period}`);
        }
      } else if (endDate != null && startDate != null) {
        monthsRange = moment(endDate).diff(startDate, 'months');
        const period = monthsToPeriod(monthsRange, proposal.monthEndDataOption);
        periodLabel = generateLocalizedString(`Common.Periods.${period}`);
      }

      break;
    }
    case ComparisonToolbarKeys.DRAWDOWN: {
      const chartOption = comparisonToolbarOptions && comparisonToolbarOptions.drawdown;

      // Always override start date for drawdown. When INC is
      // selected, startDate is intentionally left as null,
      // and it's expected that the value should remain null.
      startDate = chartOption?.startDate ?? null;

      if (chartOption && chartOption.endDate) {
        ({ endDate } = chartOption);

        if (startDate && !periodLabel) {
          monthsRange = moment(chartOption.endDate).diff(chartOption.startDate, 'months');
          const period = monthsToPeriod(monthsRange, proposal.monthEndDataOption);
          periodLabel = generateLocalizedString(`Common.Periods.${period}`);
        }
      }

      break;
    }
    case ComparisonToolbarKeys.INCOME: {
      const chartOption = comparisonToolbarOptions && comparisonToolbarOptions.income;

      if (chartOption) {
        if (chartOption.startDate) {
          ({ startDate } = chartOption);
        }

        if (chartOption.endDate) {
          ({ endDate } = chartOption);
        }

        if (startDate && endDate && !periodLabel) {
          const months = moment(endDate).diff(startDate, 'months');
          monthsRange = months;
          const period = monthsToPeriod(months, proposal.monthEndDataOption);
          periodLabel = generateLocalizedString(`Common.Periods.${period}`);
        }
      }

      break;
    }
    case ComparisonToolbarKeys.RISK_METRICS:
    case ComparisonToolbarKeys.ADVANCED_RISK_METRICS: {
      const defaultRiskPeriod = comparisonToolbarOptions && comparisonToolbarOptions.riskPeriod;
      handleRiskMetrics(defaultRiskPeriod);
      break;
    }

    case ComparisonToolbarKeys.FUND_RISK_PERIOD: {
      const defaultRiskPeriod = comparisonToolbarOptions && comparisonToolbarOptions.fundRiskPeriod;
      handleRiskMetrics(defaultRiskPeriod);
      break;
    }
    case ComparisonToolbarKeys.PORTFOLIO_RISK_PERIOD: {
      const defaultRiskPeriod = comparisonToolbarOptions && comparisonToolbarOptions.portfolioRiskPeriod;
      handleRiskMetrics(defaultRiskPeriod);
      break;
    }
    case ComparisonToolbarKeys.GROUP_RISK_PERIOD: {
      const defaultRiskPeriod = comparisonToolbarOptions && comparisonToolbarOptions.groupRiskPeriod;
      handleRiskMetrics(defaultRiskPeriod);
      break;
    }
    case ComparisonToolbarKeys.CALENDAR_YEAR_RETURNS: {
      const defaultCalendarYearPeriod = (comparisonToolbarOptions && comparisonToolbarOptions.calendarYearPeriod) || '5Y';
      handleCalendarYearReturns(defaultCalendarYearPeriod);
      break;
    }
    case ComparisonToolbarKeys.FUND_CALENDAR_YEAR_PERIOD: {
      const defaultCalendarYearPeriod = (comparisonToolbarOptions && comparisonToolbarOptions.fundCalendarYearPeriod) || '5Y';
      handleCalendarYearReturns(defaultCalendarYearPeriod);
      break;
    }
    case ComparisonToolbarKeys.PORTFOLIO_CALENDAR_YEAR_PERIOD: {
      const defaultCalendarYearPeriod = (comparisonToolbarOptions && comparisonToolbarOptions.portfolioCalendarYearPeriod) || '5Y';
      handleCalendarYearReturns(defaultCalendarYearPeriod);
      break;
    }
    case ComparisonToolbarKeys.GROUP_CALENDAR_YEAR_PERIOD: {
      const defaultCalendarYearPeriod = (comparisonToolbarOptions && comparisonToolbarOptions.groupCalendarYearPeriod) || '5Y';
      handleCalendarYearReturns(defaultCalendarYearPeriod);
      break;
    }
  }

  return { capital, startDate, endDate, rebalancePeriod, periodLabel, useMonthly, targetROI, monthsRange, indexedOptions: {} };
};

export const monthsToPeriodFromValues = (
  dateRange: Nullable<number>, // derived from startDate
  monthsRange: Nullable<number> | undefined, // from the tollbarOptions
  values: TIME_PERIODS[],
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS,
) => {
  if (dateRange == null) {
    if (monthsRange === PERIODS_VALUE_CI && values.includes(TIME_PERIODS.CI)) {
      return TIME_PERIODS.CI;
    }
    if (values.includes(TIME_PERIODS.INC)) {
      return TIME_PERIODS.INC;
    }
    return TIME_PERIODS.CI;
  }

  return monthsToPeriod(dateRange, monthEndDataOption);
};
